import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  NgZone,
  OnInit,
  output,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Model, FunctionFactory } from 'survey-core';
import { SurveyModule } from 'survey-angular-ui';
import { FormSubmissionService } from '../../services/form-submission.service';
import { LeadCaptureService } from '../../services/lead-capture.service';
import { extractWidgetApiErrorMessage } from '../../services/widget-api-error.util';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { isAadhaarValid, isEmiratesIdValid } from '../../validations';
import { LeadCaptureWidgetSubmission } from '../../interfaces/lead-capture-submission.interface';
import {
  applyTourBookingSurveyTweaks,
  configureSurveyForRepeatSubmission,
  mapSurveyDataToLeadCapturePayload,
  resolveTenantUuid,
  TENANT_FIELD_NAMES,
} from '../../services/tour-booking-submission.util';
import { WidgetRecaptchaFlowService } from '../../services/widget-recaptcha-flow.service';
import { WidgetRecaptchaSubmitPayload } from '../../services/recaptcha/widget-recaptcha.types';
import { WidgetRecaptchaV2SectionComponent } from '../../components/widget-recaptcha-v2-section/widget-recaptcha-v2-section.component';
import { WidgetProcessingOverlayComponent } from '../../components/widget-processing-overlay/widget-processing-overlay.component';
import { WIDGET_RECAPTCHA_V2_HINT_LEAD_CAPTURE } from '../../constants/checkout-security.constants';

@Component({
  selector: 'ufp-lead-capture-v2',
  templateUrl: './lead-capture-v2.component.html',
  styleUrls: ['./lead-capture-v2.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    SurveyModule,
    WidgetRecaptchaV2SectionComponent,
    WidgetProcessingOverlayComponent,
  ],
})
export class LeadCaptureV2Component implements OnInit {
  readonly widgetId = input('');
  readonly base_api_uri = input<string | undefined>(undefined);
  /** Tour booking widget only: parent handles submit and slot selection. */
  readonly tourBookingEmbed = input(false);
  /** Trial booking widget only: parent handles submit and trial date. */
  readonly trialBookingEmbed = input(false);
  readonly defaultTenantUuid = input('');

  /** Tour or trial booking embed: parent owns submit. */
  readonly isBookingEmbed = computed(
    () => this.tourBookingEmbed() || this.trialBookingEmbed(),
  );

  /** Emits when required survey fields are filled (booking embed parents). */
  readonly tourFormCompleteChange = output<boolean>();

  /** Emits when the tenant selection changes (booking embed parents). */
  readonly tenantChange = output<string>();

  model!: Model;
  loading = false;
  error = '';
  success = false;
  isSubmitting = false;

  private readonly formSubmissionService = inject(FormSubmissionService);
  private readonly leadCaptureService = inject(LeadCaptureService);
  readonly recaptchaFlow = inject(WidgetRecaptchaFlowService);
  readonly recaptchaV2Hint = WIDGET_RECAPTCHA_V2_HINT_LEAD_CAPTURE;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private companyUuid = '';
  private fallbackTenantUuid = '';

  /** HttpClient callbacks can run outside NgZone in embed/custom-element apps. */
  private notifyViewUpdate(): void {
    this.cdr.markForCheck();
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.widgetId()) {
      this.error = 'Widget ID is required';
      this.notifyViewUpdate();
      return;
    }

    FunctionFactory.Instance.register('isAadhaarValid', isAadhaarValid);
    FunctionFactory.Instance.register('isEmiratesIdValid', isEmiratesIdValid);

    this.initializeWidget();
  }

  /** Used by tour/trial booking embed parents. */
  isTourBookingFormComplete(): boolean {
    if (!this.isBookingEmbed() || !this.model) {
      return false;
    }

    return this.model.getAllQuestions(true).every((question) => {
      if (!question.isVisible || !question.isRequired) {
        return true;
      }
      return !question.isEmpty();
    });
  }

  /** Used by tour booking widget only. */
  getLeadCaptureSubmissionPayload(): LeadCaptureWidgetSubmission | null {
    if (!this.isBookingEmbed() || !this.model?.validate()) {
      return null;
    }

    return mapSurveyDataToLeadCapturePayload(
      this.model.data as Record<string, unknown>,
      this.defaultTenantUuid(),
      this.model,
    );
  }

  /** Used by tour booking widget only. */
  resetSurvey(): void {
    if (!this.isBookingEmbed() || !this.model) {
      return;
    }

    this.model.clear();
    configureSurveyForRepeatSubmission(this.model);
    this.model.render();
    this.isSubmitting = false;
    if (this.isBookingEmbed()) {
      this.tourFormCompleteChange.emit(false);
    }
    this.notifyViewUpdate();
  }

  onRecaptchaV2Resolved(token: string): void {
    this.recaptchaFlow.onV2Resolved(token);
    this.notifyViewUpdate();
  }

  onRecaptchaV2Failed(): void {
    this.recaptchaFlow.onV2Failed();
    this.error = 'Security verification failed. Please try again.';
    this.isSubmitting = false;
    this.notifyViewUpdate();
  }

  private initializeWidget() {
    this.loading = true;
    this.notifyViewUpdate();
    this.leadCaptureService
      .getWidgetConfig(this.widgetId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (widgetConfig) => {
        try {
          if (
            widgetConfig.widget_type !== 'LeadCapture' &&
            widgetConfig.widget_type !== 'TourBooking' &&
            widgetConfig.widget_type !== 'TrialBooking'
          ) {
            this.error = 'Failed to load the widget: Widget configuration mismatch';
            this.loading = false;
            this.notifyViewUpdate();
            return;
          }

          this.companyUuid = widgetConfig.widget_company_uuid ?? '';
          this.fallbackTenantUuid =
            widgetConfig.widget_enabled_tenants?.[0]?.tenant_uuid ?? '';
          const enabledTenants = widgetConfig.widget_enabled_tenants ?? [];

          const survey = new Model(widgetConfig.widget_data);
          configureSurveyForRepeatSubmission(survey);

          if (widgetConfig.widget_data?.themeJson) {
            survey.applyTheme(widgetConfig.widget_data.themeJson);
          }

          survey.goNextPageAutomatic = false;
          survey.completeText = 'Submit';
          survey.showCompletedPage = false;

          this.attachTenantRecaptchaPrepareListeners(survey, enabledTenants);

          if (this.isBookingEmbed()) {
            survey.showCompleteButton = false;
            applyTourBookingSurveyTweaks(survey);
            this.attachTourBookingFormListeners(survey);
          } else {
            survey.onCompleting.add((sender, options) => {
              if (this.isSubmitting) {
                options.allowComplete = false;
                return;
              }

              options.allowComplete = false;
              this.isSubmitting = true;
              this.error = '';
              const processedData = mapSurveyDataToLeadCapturePayload(
                sender.data as Record<string, unknown>,
                '',
                sender,
              );

              this.notifyViewUpdate();
              void this.submitLeadCaptureForm(sender, processedData);
            });
          }

          this.model = survey;
          this.loading = false;
          this.notifyViewUpdate();
        } catch (error) {
          console.error('Failed to create survey model:', error);
          this.error = 'Failed to initialize widget. Please try again later.';
          this.loading = false;
          this.notifyViewUpdate();
        }
      },
      error: (error) => {
        console.error('Failed to initialize widget:', error);
        this.error = 'Failed to initialize widget. Please try again later.';
        this.loading = false;
        this.notifyViewUpdate();
      },
    });
  }

  private attachTenantRecaptchaPrepareListeners(
    survey: Model,
    enabledTenants: { tenant_uuid?: string }[],
  ): void {
    const prepare = () => {
      const tenantUuid = this.resolveTenantUuidForRecaptcha(
        survey,
        enabledTenants,
      );
      if (tenantUuid && this.companyUuid) {
        void this.recaptchaFlow.prepareForTenant(this.companyUuid, tenantUuid);
      }
    };

    survey.onValueChanged.add(() => {
      prepare();
    });

    prepare();
  }

  private resolveTenantUuidForRecaptcha(
    survey: Model,
    enabledTenants: { tenant_uuid?: string }[],
  ): string {
    const fromPayload = mapSurveyDataToLeadCapturePayload(
      survey.data as Record<string, unknown>,
      '',
      survey,
    ).data.tenant_uuid?.trim();
    if (fromPayload) {
      return fromPayload;
    }
    if (enabledTenants.length === 1) {
      return enabledTenants[0]?.tenant_uuid?.trim() ?? '';
    }
    return '';
  }

  private attachTourBookingFormListeners(survey: Model): void {
    const notifyParent = () => {
      this.tourFormCompleteChange.emit(this.isTourBookingFormComplete());
    };

    const notifyTenantChange = () => {
      const tenantUuid = resolveTenantUuid(
        survey.data as Record<string, unknown>,
      );
      if (tenantUuid) {
        this.tenantChange.emit(tenantUuid);
      }
    };

    survey.onValueChanged.add((_sender, options) => {
      notifyParent();
      if (
        TENANT_FIELD_NAMES.includes(
          options.name as (typeof TENANT_FIELD_NAMES)[number],
        )
      ) {
        notifyTenantChange();
      }
    });
    survey.onValidatedErrorsOnCurrentPage.add(notifyParent);
    notifyParent();
    notifyTenantChange();
  }

  private async submitLeadCaptureForm(
    sender: Model,
    processedData: LeadCaptureWidgetSubmission,
  ): Promise<void> {
    const tenantUuid =
      processedData.data.tenant_uuid?.trim() || this.fallbackTenantUuid;

    await this.recaptchaFlow.beginSubmit(
      this.companyUuid,
      tenantUuid,
      (captcha) => {
        this.executeLeadCaptureSubmit(sender, processedData, captcha);
      },
    );
    this.notifyViewUpdate();
  }

  private executeLeadCaptureSubmit(
    sender: Model,
    processedData: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): void {
    this.formSubmissionService
      .submitForm(this.widgetId(), processedData, undefined, captcha)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.success = true;
          this.recaptchaFlow.resetChallenge();
          const tenantUuid =
            processedData.data.tenant_uuid?.trim() || this.fallbackTenantUuid;
          if (tenantUuid && this.companyUuid) {
            void this.recaptchaFlow.prepareForTenant(
              this.companyUuid,
              tenantUuid,
            );
          }
          sender.clear(false, false);
          this.isSubmitting = false;
          this.notifyViewUpdate();
        },
        error: (error) => {
          if (
            this.recaptchaFlow.handleRecaptchaError(error, (retryCaptcha) =>
              this.executeLeadCaptureSubmit(sender, processedData, retryCaptcha),
            )
          ) {
            this.isSubmitting = false;
            this.notifyViewUpdate();
            return;
          }

          this.error =
            extractWidgetApiErrorMessage(error.error) ??
            'Failed to submit form. Please try again.';
          this.isSubmitting = false;
          this.notifyViewUpdate();
        },
      });
  }
}
